/**
 * EnrichmentFlow - Chat-style enrichment setup with typewriter effect
 * Allows users to add AI-enriched columns via conversational interface
 */
import React, { useState, useEffect, useRef } from 'react';
import { Check, Loader2, Sparkles, AlertCircle, RefreshCw, ChevronDown } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { parseError } from '../../utils/errorUtils';
import { CreditPill } from '../ui';
import { calculateEnrichmentCost } from '../../constants/credits';
import { AssistantMessage, UserMessage } from './shared';

interface EnrichmentMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface EnrichmentFlowProps {
  tableId: string;
  columns: string[];
  rowCount: number;
  onEnrichmentStarted?: (columnName: string) => void;
  onAddMessages?: (messages: EnrichmentMessage[]) => void;
  preSelectedColumns?: string[]; // Columns to pre-select for enrichment (e.g., from campaign flow)
  triggeredFromChat?: boolean; // When true, skip showing initial "Enrich" user message (user already typed something)
}

type SetupStep = 'intro' | 'typing_intro' | 'configure' | 'creating' | 'done' | 'completed' | 'error';

interface ErrorInfo {
  message: string;
  details?: string;
  isDuplicateColumn?: boolean;
}

const FORMAT_PRESETS = [
  { value: 'none', label: 'No specific format' },
  { value: 'sentence', label: 'One concise sentence' },
  { value: 'tagline', label: 'Short tagline' },
  { value: 'date', label: 'Date (YYYY-MM-DD)' },
  { value: 'summary', label: 'Brief summary (max 2 lines)' },
  { value: 'custom', label: 'Custom format' },
];

export const EnrichmentFlow: React.FC<EnrichmentFlowProps> = ({
  tableId,
  columns,
  rowCount,
  onEnrichmentStarted,
  onAddMessages,
  preSelectedColumns = [],
  triggeredFromChat = false,
}) => {
  const { showToast } = useToast();
  const { openSettings } = useSettingsModal();
  const [step, setStep] = useState<SetupStep>('intro');
  // Pre-populate column name if pre-selected columns are provided
  const [columnName, setColumnName] = useState(preSelectedColumns.length > 0 ? preSelectedColumns[0] : '');
  const [formatPreset, setFormatPreset] = useState('none');
  const [customInstructions, setCustomInstructions] = useState('');
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [displayedIntroText, setDisplayedIntroText] = useState('');
  const [introComplete, setIntroComplete] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Dynamic intro text based on context
  const getIntroText = () => {
    if (triggeredFromChat && preSelectedColumns.length > 0) {
      return `Got it! I'll enrich your dataset with "${preSelectedColumns.join('", "')}" data. Let me set that up for you.`;
    }
    if (preSelectedColumns.length > 0) {
      return `I see your email template needs "${preSelectedColumns.join('", "')}" data. Let me help you enrich your dataset with this information!`;
    }
    return `I'd love to help you enrich your dataset! Just tell me what new information you'd like to add for each row, and I'll use AI to research and fill it in automatically.`;
  };
  const introText = getIntroText();

  // Typewriter effect for intro message - using requestAnimationFrame for smoother performance
  useEffect(() => {
    if (step === 'intro') {
      setDisplayedIntroText('');
      setIntroComplete(false);
      let index = 0;
      let lastTime = 0;
      const charDelay = 18; // ms per character
      let animationId: number;

      const animate = (currentTime: number) => {
        if (!lastTime) lastTime = currentTime;
        const elapsed = currentTime - lastTime;

        if (elapsed >= charDelay) {
          if (index < introText.length) {
            // Update multiple characters at once if we've fallen behind
            const charsToAdd = Math.min(Math.floor(elapsed / charDelay), introText.length - index);
            index += charsToAdd;
            setDisplayedIntroText(introText.slice(0, index));
            lastTime = currentTime;
          } else {
            setIntroComplete(true);
            setTimeout(() => inputRef.current?.focus(), 100);
            return;
          }
        }

        animationId = requestAnimationFrame(animate);
      };

      animationId = requestAnimationFrame(animate);

      return () => {
        if (animationId) cancelAnimationFrame(animationId);
      };
    }
  }, [step, introText]);

  // Auto-scroll when step changes
  useEffect(() => {
    const timer = setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timer);
  }, [step, displayedIntroText]);

  const handleColumnNameSubmit = () => {
    if (!columnName.trim()) return;
    setStep('configure');
  };

  const handleCreate = async () => {
    if (!columnName.trim()) return;

    setStep('creating');
    setErrorInfo(null);

    try {
      // Build format hint
      let formatHint = '';
      switch (formatPreset) {
        case 'sentence':
          formatHint = 'Respond in one concise sentence.';
          break;
        case 'tagline':
          formatHint = 'Respond with a short tagline or phrase.';
          break;
        case 'date':
          formatHint = 'Respond with a date in YYYY-MM-DD format.';
          break;
        case 'summary':
          formatHint = 'Respond with a brief summary, maximum 2 lines.';
          break;
        case 'custom':
          formatHint = customInstructions;
          break;
      }

      // Step 1: Create the column first
      await apiClient.ensureTableColumn(tableId, columnName.trim(), 'text');

      // Step 2: Start enrichment on the column
      await apiClient.enrichTableColumn(tableId, {
        column_name: columnName.trim(),
        row_limit: 'all',
        format_hint: formatHint || undefined,
        allow_creative_phrasing: true
      });

      // Notify parent that enrichment has started
      if (onEnrichmentStarted) {
        onEnrichmentStarted(columnName.trim());
      }

      // Save flow messages to chat history
      if (onAddMessages) {
        const now = new Date();
        const messages: EnrichmentMessage[] = [];

        // Only add the initial "Enrich" message if NOT triggered from chat
        // (when triggered from chat, the user's message is already in the chat history)
        if (!triggeredFromChat) {
          messages.push({ id: `enrich-user-${now.getTime()}`, role: 'user', content: 'Enrich', timestamp: now });
        }

        messages.push(
          { id: `enrich-assistant-intro-${now.getTime()}`, role: 'assistant', content: introText, timestamp: now },
          { id: `enrich-user-column-${now.getTime()}`, role: 'user', content: columnName.trim(), timestamp: now },
          { id: `enrich-assistant-success-${now.getTime()}`, role: 'assistant', content: `Done! The "${columnName.trim()}" column has been added. AI enrichment is running on ${rowCount} rows. You'll see results appear in real-time.`, timestamp: now }
        );

        onAddMessages(messages);
      }

      setStep('done');
      showToast(`Column "${columnName}" added! AI enrichment started.`, 'success');
    } catch (error: any) {
      console.error('Failed to create enrichment column:', error);

      const err = parseError(error);

      if (err.requiresUpgrade || err.shouldReturnToIntro) {
        showToast(err.message, {
          type: 'error',
          duration: 8000,
          action: { label: 'Upgrade', onClick: () => openSettings('billing') }
        });
      } else {
        showToast(err.message, 'error', 5000);
      }

      setErrorInfo({ message: err.message, details: err.details, isDuplicateColumn: err.isDuplicateColumn });
      setStep('error');
    }
  };

  const handleRetry = () => {
    // For duplicate column errors, go back to intro so user can change the name
    if (errorInfo?.isDuplicateColumn) {
      setStep('intro');
      setIntroComplete(true); // Skip typewriter animation on retry
    } else {
      setStep('configure');
    }
    setErrorInfo(null);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Chat content section */}
      <div className="flex-shrink-0 space-y-4">
        {/* Only show initial "Enrich" message if NOT triggered from chat (user already typed something) */}
        {!triggeredFromChat && <UserMessage>Enrich</UserMessage>}

        {/* Message 1: Intro with typewriter effect */}
        <AssistantMessage>
          <p className="text-[15px] text-gray-800 leading-relaxed">
            {displayedIntroText}
            {!introComplete && <span className="inline-block w-0.5 h-4 bg-gray-400 ml-0.5 animate-pulse" />}
          </p>
        </AssistantMessage>

      {/* Input for column name (visible in intro step after typewriter) */}
      {step === 'intro' && introComplete && (
        <div className="ml-10 animate-message-in" style={{ animationDelay: '100ms' }}>
          <div className="flex flex-col gap-3">
            <input
              ref={inputRef}
              type="text"
              value={columnName}
              onChange={(e) => setColumnName(e.target.value)}
              placeholder="e.g., Revenue, Industry, Employees..."
              className="w-full px-4 py-2.5 text-[14px] border border-gray-200 rounded-xl outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-100 transition-all"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && columnName.trim()) {
                  handleColumnNameSubmit();
                }
              }}
            />
            <button
              onClick={handleColumnNameSubmit}
              disabled={!columnName.trim()}
              className={`w-full px-4 py-2.5 text-[13px] font-medium rounded-xl transition-all active:scale-[0.98] ${
                columnName.trim()
                  ? 'bg-gray-900 text-white hover:bg-gray-800'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* User message: Column name (visible after intro) */}
      {step !== 'intro' && (
        <UserMessage>{columnName}</UserMessage>
      )}

      {/* Configure step */}
      {step === 'configure' && (
        <>
          <AssistantMessage>
            <p className="text-[15px] text-gray-800 leading-relaxed">
              Great choice! I'll add a "<strong>{columnName}</strong>" column and enrich it for all {rowCount} rows. Would you like to customize the format?
            </p>
          </AssistantMessage>

          {/* Config Card */}
          <div className="ml-10 mt-3 animate-message-in" style={{ animationDelay: '250ms' }}>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-4">
              {/* Format Preset */}
              <div>
                <p className="text-[11px] font-medium text-gray-500 mb-2">Output Format</p>
                <div className="relative">
                  <select
                    value={formatPreset}
                    onChange={(e) => setFormatPreset(e.target.value)}
                    className="w-full px-3 py-2 text-[13px] bg-white border border-gray-200 rounded-lg appearance-none cursor-pointer focus:outline-none focus:border-gray-400 transition-colors"
                  >
                    {FORMAT_PRESETS.map(preset => (
                      <option key={preset.value} value={preset.value}>{preset.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Custom Instructions */}
              {formatPreset === 'custom' && (
                <div className="animate-fade-in">
                  <p className="text-[11px] font-medium text-gray-500 mb-2">Custom Instructions</p>
                  <textarea
                    value={customInstructions}
                    onChange={(e) => setCustomInstructions(e.target.value)}
                    placeholder="e.g., 'Respond in bullet points', 'Use only numbers', 'Format as a question'..."
                    rows={3}
                    className="w-full px-3 py-2 text-[13px] bg-white border border-gray-200 rounded-lg resize-none focus:outline-none focus:border-gray-400 transition-colors"
                  />
                </div>
              )}

              {/* Existing columns hint */}
              {columns.length > 0 && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-[11px] text-gray-400">
                    Existing columns: {columns.slice(0, 5).join(', ')}{columns.length > 5 ? `, +${columns.length - 5} more` : ''}
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center mt-3">
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-4 py-2 text-[13px] font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors active:scale-[0.98]"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Start Enrichment
                <CreditPill cost={calculateEnrichmentCost(rowCount)} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Creating state */}
      {step === 'creating' && (
        <>
          <UserMessage>
            {formatPreset === 'none' ? 'Start enrichment' : `Format: ${FORMAT_PRESETS.find(p => p.value === formatPreset)?.label}`}
          </UserMessage>
          <AssistantMessage>
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
              <p className="text-[15px] text-gray-600">Creating column and starting enrichment...</p>
            </div>
          </AssistantMessage>
        </>
      )}

      {/* Success state */}
      {(step === 'done' || step === 'completed') && (
        <>
          <UserMessage>
            {formatPreset === 'none' ? 'Start enrichment' : `Format: ${FORMAT_PRESETS.find(p => p.value === formatPreset)?.label}`}
          </UserMessage>
          <AssistantMessage>
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check className="w-3 h-3 text-gray-600" />
              </div>
              <div>
                <p className="text-[15px] text-gray-800">
                  Done! The "<strong>{columnName}</strong>" column has been added.
                </p>
                <p className="text-[13px] text-gray-500 mt-0.5">
                  AI enrichment is running on {rowCount} rows. You'll see results appear in real-time.
                </p>
              </div>
            </div>

            {step === 'done' && (
              <div className="flex mt-3">
                <button
                  onClick={() => {
                    // Reset for another enrichment
                    setColumnName('');
                    setFormatPreset('none');
                    setCustomInstructions('');
                    setStep('intro');
                  }}
                  className="px-4 py-2 text-[13px] font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-all active:scale-[0.98]"
                >
                  Add another enrichment
                </button>
              </div>
            )}
          </AssistantMessage>
        </>
      )}

      {/* Error state */}
      {step === 'error' && errorInfo && (
        <>
          <UserMessage>
            {formatPreset === 'none' ? 'Start enrichment' : `Format: ${FORMAT_PRESETS.find(p => p.value === formatPreset)?.label}`}
          </UserMessage>
          <AssistantMessage>
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <AlertCircle className="w-3 h-3 text-gray-500" />
              </div>
              <div>
                <p className="text-[15px] text-gray-800">
                  {errorInfo.message}
                </p>
                {errorInfo.details && (
                  <p className="text-[13px] text-gray-500 mt-0.5">
                    {errorInfo.details}
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2 mt-3">
              {parseError({ message: errorInfo.message }).requiresUpgrade ? (
                <button
                  onClick={() => openSettings('billing')}
                  className="flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-all active:scale-[0.98]"
                >
                  Upgrade Plan
                </button>
              ) : (
                <button
                  onClick={handleRetry}
                  className="flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-all active:scale-[0.98]"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  {errorInfo.isDuplicateColumn ? 'Choose Different Name' : 'Try Again'}
                </button>
              )}
            </div>
          </AssistantMessage>
        </>
      )}

        {/* Scroll anchor */}
        <div ref={bottomRef} />
      </div>

      {/* Spacer to fill remaining space */}
      <div className="flex-grow min-h-[20px]" />

      <style>{`
        @keyframes messageIn {
          0% {
            opacity: 0;
            transform: translateY(12px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .animate-message-in {
          animation: messageIn 400ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        .animate-fade-in {
          animation: fadeIn 200ms ease-out both;
        }
      `}</style>
    </div>
  );
};

export default EnrichmentFlow;
